import { useState, useEffect, useMemo, useCallback } from "react";
import middleware from "../../../Api/Middleware";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router";
import "../../../Main/Main.css";
import ModalColorAdd from "../../../Products/subpages/sub_components/VariationModals";
import PageTemplate from "../../../Templates/PageTemplate";
import TablePreset from "../../../Table/TablePreset";
import CheckboxHeader from "../../../Table/TableComponents/CheckboxHeader";
import CheckboxCell from "../../../Table/TableComponents/CheckboxCell";
import SortableHeader from "../../../Table/TableComponents/SortableHeader";
import { useTable } from "react-table";
import ConfirmationModal from "../../../Modals/GeneralSettings/ConfirmationModal";
import tinycolor from "tinycolor2";
import Filters from "../../../Filters/Filters";
import { BackButton } from "../../../HouseStyle/Components/Buttons";
import ToastPromise from "../../../Toasts/ToastPromise";

function ColorSettings() {
  const navigate = useNavigate();
  const cookies = new Cookies();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true); // useState for page loading
  const [addNewColor, setAddNewColor] = useState(false);
  const [editColor, setEditColor] = useState();
  const [colors, setColors] = useState([]); // useState for all products in the database
  const [showEditColorModal, setShowEditColorModal] = useState(false);
  const [pagination, setPagination] = useState({
    page_size: 10,
    page: 1,
    max_items: 0,
    begin_of_page: 0,
    end_of_page: 0,
  }); // the useState for the pagination for the table

  const [selectedIds, setSelectedIds] = useState([]);
  const [order, setOrder] = useState({ orderBy: '', direction: '' }); // useState for ordering the columns in the table
  const [searchBar, setSearchBar] = useState("");
  const defaultFilters = [
    {
      index: 0,
      order: 1,
      value: 1, // default to published
      name: "Status kleur",
      field: "status",
      type: "radio",
      options: [
        { name: "Gepubliceerd", value: 1 },
        { name: "Gearchiveerd", value: 0 },
      ],
    },
  ];
  const [tempfilters, setTempFilters] = useState(defaultFilters);
  const [filters, setFilters] = useState(defaultFilters);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // fetched all the products and the stock an the colors from the database
  const fetchAll = async () => {
    try {
      setLoading(true);
      const searchQuery = searchBar.trim() ? `&searchTerm=${encodeURIComponent(searchBar.trim())}&noSearch=[color_id,status,sorting_order,updated_at,created_at,updater_user_id,updater_user_id]` : "";
      // Build filter query in the format expected by PHP filtering system
      const filterQuery = filters
        .filter((e) => e.value != null && e.value !== "")
        .map((e) => `${e.field}=${e.value}`)
        .join("|"); // Use | separator as expected by PHP
      // Add filtering parameter if filters exist
      const filterParam = filterQuery ? `&filtering=${encodeURIComponent(filterQuery)}` : "";
      const [fetchColors] = await Promise.all([
        middleware.get(`sp/colorsTableAll?page=${pagination.page}&page_size=${pagination.page_size}${order.orderBy !== '' && order.direction !== '' ? `&orderBy=${order.orderBy}&orderDirection=${order.direction}` : ''}${searchQuery}${filterParam}`),
      ]);
      setColors(Array.isArray(fetchColors.data.data) ? fetchColors.data.data : []); // sets the colors usestate with the fetched colors
      setPagination({
        ...pagination,
        max_items: fetchColors.data.total,
        begin_of_page: fetchColors.data.from,
        end_of_page: fetchColors.data.to,
      }); // sets the pagination usestate with the max items begin of the page and the end of the page
      setLoading(false);
    } catch (error) {
      if (error.response.status === 401) {
        cookies.remove("token", { path: "/" });
        navigate("/");
      }
    }
  };

  // function that does an api call to only get the colors the user searched for and updates whenever the colorsearchengine changes
  useEffect(() => {
    try {
      if (searchBar.trim() === "" && searchBar === "") {
        // Code to fetch all when searchengine is empty
        fetchAll();
      } else if (searchBar.trim() !== "") {
        fetchAll();
      }
    } catch (error) {
      if (error.response.status === 401) {
        cookies.remove("token", { path: "/" });
        navigate("/");
      }
    }
  }, [searchBar]);

  // useEffect that fires every time the pagination.page_size or pagination.page changes and on every fire it uses the fetchall function
  useEffect(() => {
    fetchAll();
  }, [pagination.page_size, pagination.page, order, filters]);

  const handleSearchChange = useCallback((value) => {
    setSearchBar(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const columns = useMemo(
    () => {
      let columnsArray = [
        {
          Header: (
            <CheckboxHeader currentItem={colors} selectedIds={selectedIds} setSelectedIds={setSelectedIds} pk={'color_id'}>
            </CheckboxHeader>
          ),
          accessor: "select",
          Cell: ({ row }) => {
            return <CheckboxCell row={row} selectedIds={selectedIds} pk={'color_id'} handleSelection={handleSelection} />
          }
        },
        {
          Header: () => (
            <SortableHeader currentItem={order} setItem={setOrder} field="name" pos={'start'}>
              Kleur naam
            </SortableHeader>
          ),
          accessor: "name",
          Cell: ({ row }) => {
            return <div className="text-start FlatTextLink" onClick={() => { setEditColor(row.original); setShowEditColorModal(true); }}>{row.original.name}</div>;
          }
        },
        {
          Header: () => (
            <SortableHeader currentItem={order} setItem={setOrder} field="hex" pos={'start'}>
              Kleur
            </SortableHeader>
          ),
          accessor: "hex",
          Cell: ({ row }) => {
            return <div style={{ width: "100%", height: "20px", backgroundColor: getValidColor(row.original.hex), border: "1px solid #000", }}></div>;
          }
        },
        {
          Header: () => (
            <SortableHeader
              currentItem={order}
              setItem={setOrder}
              field={"status"}
              pos={"end"}
            >
              Status
            </SortableHeader>
          ),
          accessor: "status",
          Cell: ({ row }) => {
            return <div className="text-end" style={{ color: row.original.status == "1" ? "#A9D974" : "#ffcc9d" }}>
              {row.original.status == "1" ? "Gepubliceerd" : "Gearchiveerd"}
            </div>
          }
        },
      ];
      return columnsArray;
    },
    [order.orderBy, order.direction, selectedIds, setSelectedIds, colors]
  );

  //   Table instance
  const tableInstance = useTable({ columns, data: colors });

  const handleSelection = (sizeId, isSelected) => {
    if (isSelected) {
      setSelectedIds([...selectedIds, sizeId]);
    } else {
      setSelectedIds(selectedIds.filter(id => id !== sizeId));
    }
  };

  // closes the editmodal
  const closingModal = () => {
    setShowEditColorModal(false);
  };

  const closeColorEditModal = (e) => {
    setShowEditColorModal(e);
  };

  const CloseFilterModal = () => {
  setShowFilterModal(false);
};

  const deactivateColors = async () => {
    try {
      const promises = selectedIds.map(id =>
        middleware.put(`products/colors`, {
          color_id: id,
          status: 0,
        }));
      await Promise.all(promises);
      setSelectedIds([]);
      // Optionally, you can refresh the data or perform other actions here
      fetchAll();
    } catch (error) {
      console.error("Error deactivating sizes:", error);
    }
  }

  
  const activateColors = async () => {
    try {
      const promises = selectedIds.map(id =>
        middleware.put(`products/colors`, {
          color_id: id,
          status: 1,
        }));
      await Promise.all(promises);
      setSelectedIds([]);
      // Optionally, you can refresh the data or perform other actions here
      fetchAll();
    } catch (error) {
      console.error("Error deactivating sizes:", error);
    }
  }

  const getValidColor = (color) => {
    // Converteer de kleur naar een hex-string, ongeacht het oorspronkelijke formaat.
    return tinycolor(color).toRgbString();
  };

  const DeleteColors = async () => {
    await ToastPromise({
      promise: (async () => {
        // Execute delete request
        const response = await middleware.delete(`products/colors?color_ids=${selectedIds.join(',')}`);
        
        // Check if any colors were actually deleted
        if (response.data?.summary) {
          const { total, deleted, errors } = response.data.summary;
          
          // Handle different scenarios
          if (deleted === 0 && errors > 0) {
            // Nothing deleted, all errors
            throw new Error('no_deletions');
          }
          
          if (deleted > 0) {
            // At least some were deleted, refresh the list
            fetchAll();
            
            // Update selection to only keep failed IDs
            if (errors > 0) {
              const failedIds = response.data.results
                .filter(r => r.status === 'error')
                .map(r => r.color_id);
            }
          }
        }
        
        setSelectedIds([]);

        return response;
      })(),
      pending: 'Kleuren worden verwijderd...',
      successRenderer: (response) => {
        const data = response?.data;
        
        if (!data?.summary) {
          return 'Kleuren verwijderd';
        }
        
        const { total, deleted, errors } = data.summary;
        
        // Build success message based on results
        if (deleted === total) {
          // All successfully deleted
          return deleted === 1 
            ? 'Kleur succesvol verwijderd' 
            : `Alle ${deleted} kleuren succesvol verwijderd`;
        }
        
        if (deleted > 0 && errors > 0) {
          // Partial success
          const inUseCount = data.results.filter(r => 
            r.error?.includes('in use')
          ).length;
          
          if (inUseCount === errors) {
            return `${deleted} van ${total} kleuren verwijderd. ${errors} ${errors === 1 ? 'kleur is' : 'kleuren zijn'} nog in gebruik.`;
          }
          
          return `${deleted} van ${total} kleuren verwijderd. ${errors} ${errors === 1 ? 'fout' : 'fouten'} opgetreden.`;
        }
        
        // Should not reach here if throw worked correctly
        return 'Actie voltooid';
      },
      errorRenderer: (error) => {
        console.error("Error deleting colors:", error);
        
        // Check if it's our custom error for no deletions
        if (error?.message === 'no_deletions') {
          const response = error.response || {};
          const data = response.data || {};
          
          if (data.summary?.errors > 0) {
            const inUseCount = data.results?.filter(r => 
              r.error?.includes('in use')
            ).length || 0;
            
            if (inUseCount === data.summary.errors) {
              return data.summary.errors === 1
                ? 'Deze kleur kan niet worden verwijderd omdat deze nog aan een of meer producten is gekoppeld.'
                : `Deze ${data.summary.errors} kleuren kunnen niet worden verwijderd omdat ze nog aan een of meer producten zijn gekoppeld.`;
            }
          }
          // Altijd een duidelijke melding geven als fallback
          return 'De geselecteerde kleuren kunnen niet worden verwijderd omdat ze nog aan een of meer producten zijn gekoppeld.';
        }
        
        // Handle HTTP error codes
        if (error?.response?.status === 403) {
          return 'Je hebt geen toestemming om deze kleuren te verwijderen';
        }
        
        if (error?.response?.status === 404) {
          return 'Een of meer kleuren niet gevonden';
        }
        
        return 'Fout bij het verwijderen van kleuren. Probeer het opnieuw.';
      },
      autoClose: 4000
    });
  };

  return (
    <>
      <ModalColorAdd
        handleClose={closingModal}
        openEditModal={showEditColorModal}
        editColor={editColor}
        callColors={false}
        closeEditModal={(e) => closeColorEditModal(e)}
        openAddColor={(e) => setAddNewColor(e)}
        addNewColor={addNewColor}
        setOpenAddColor={(e) => setAddNewColor(e)}
        redirect={true}
        updatedOrAdded={() => fetchAll()}
      />
      <ConfirmationModal showConfirmaton={show} setShowConfirmation={(e) => setShow(e)} selectedIds={selectedIds} continueAction={() => deactivateColors()} />
      <PageTemplate navbarTitle={"Kleuren Overzicht"}>
        <div className="w-100">
          <div>
            <BackButton
              text={'Terug'}
              onClickFunction={() => navigate('/algemene/instellingen/product')}
            />
          </div>
          <div className="d-flex justify-content-between flex-column mb-3 ">
            <div className=" d-flex justify-content-start w-100 flex-wrap">
              <div className="w-100">
                <TablePreset
                  data={[{ 'selectedIds': selectedIds, 'tableInstance': tableInstance, 'pagination': pagination, 'primary_key': 'color_id' }]}
                  tableLoading={loading}
                  actionOptions={[{ title: "Archiveren", function: () => setShow(true) }, { title: "Publiceren", function: () => activateColors() }, { title: "Verwijderen", function: () => DeleteColors() }]}
                  searchBar={[{ placeholder: 'Zoek op naam of kleurcode...', shown: true, searchFunction: handleSearchChange, }]}
                  handleSelection={handleSelection}
                  setPagination={setPagination}
                  // rightDropdowns={[{
                  //     placeholder: 'Filter op categorie',
                  //     options: [{title: "Optie1"}, {title: "Optie2"}]
                  // }]}
                  leftButtons={[
                    { title: "Filteren", function: () => setShowFilterModal(true) }
                  ]}
                  rightButtons={[{ title: 'Nieuwe kleur', function: () => setAddNewColor(true) }]}
                />
              </div>
            </div>
          </div>
        </div>
        <Filters
          filters={tempfilters}
          showFilterModal={showFilterModal}
          closeFilterModal={CloseFilterModal}
          setTempFilters={setTempFilters}
          setFilters={(newFilters) => {
            setFilters(newFilters);
            setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page on filter
          }}
          defaultFilters={defaultFilters}
        />
      </PageTemplate>
    </>
  );
}

export default ColorSettings;